/* components/ui/Field.jsx — семейство полей ввода (примитивы форм).
   Field         — обёртка label + hint + error-сообщение (children = контрол).
   TextInput     — базовый текстовый input (default/focus/error/disabled/readonly).
   PasswordInput — TextInput + переключатель видимости пароля.
   Textarea      — авторастягивающийся textarea (min/max строк).
   NumberInput   — число + unit-суффикс (г/₽/%) + stepper ±, валидация min/max.
   SearchInput   — input с иконкой поиска, debounce, кнопкой очистки ×.
   NativeSelect  — нативный <select> (fallback и простые случаи).
   Checkbox      — НАТИВНЫЙ <input type="checkbox"> (баг §15.5: не button role).
   Segmented     — pill-переключатель N вариантов (аналог radio-group).
   StrengthMeter — 4 сегмента силы пароля (цвет+подпись).
   Labeled       — пара label + value (read-only мета).
   ХЕНДОФФ → frontend/components/common/. */

import React from 'react';
import { Icon } from './Icon.jsx';
import { normalizeDecimalText, toCommaDecimal } from '../../lib/decimal.js';

  const { useState, useRef, useEffect, useLayoutEffect, useId, forwardRef, Children, cloneElement, isValidElement } = React;

  /* ── Field: обёртка контрола ──
     Связывает подпись САМ (K-08): id — через useId, ЕДИНСТВЕННОМУ контролу среди детей
     проставляются id, aria-describedby (подсказка или ошибка), aria-invalid (есть ошибка)
     и aria-required. Контрол — нативное поле (input/select/textarea) или компонент UI-kit
     с меткой `fieldControl` (он пробрасывает id и aria-* в свой нативный элемент). Кнопка
     рядом с полем («Скопировать из Telegram») — действие, а НЕ контрол поля. Группа в div,
     два контрола, незнакомый компонент — не связываются: label for на НЕподписываемый
     элемент хуже, чем ничего. Свой id контрола уважается; явный htmlFor без своего id —
     становится id контрола (иначе подпись вела бы в пустоту).
     Select (fieldControl === 'combobox') связывается по APG select-only combobox: имя —
     aria-labelledby (подпись + значение), подпись БЕЗ for (второй клик по ней переоткрывал
     бы список), клик по подписи только ставит фокус на триггер.

     ⚠️ Дети идут через Children.map ВСЕГДА — одним путём. Раньше при одном контроле был
     map (ключи «.0», «.1»), а при нуле/двух — дети как есть (без ключей): условный сосед
     менял ветку, ключи менялись, и React ПЕРЕМОНТИРОВАЛ поле — пропадали фокус и набор
     («Рассылки» → «Почта»: после первой буквы оставалось «П», фокус уходил на BODY). */
  const LABELABLE = new Set(['input', 'select', 'textarea']);
  const isFieldControl = (el) => isValidElement(el) && (typeof el.type === 'string'
    ? LABELABLE.has(el.type) && !(el.type === 'input' && el.props.type === 'hidden')
    : !!(el.type && el.type.fieldControl));

  function Field({ label, hint, error, required, htmlFor, children, className }) {
    const baseId = useId();
    const labelId = baseId + '-label';
    const controls = Children.toArray(children).filter(isFieldControl);
    const control = controls.length === 1 ? controls[0] : null;
    const combobox = !!(control && control.type && control.type.fieldControl === 'combobox');
    const msgId = error ? baseId + '-error' : (hint ? baseId + '-hint' : undefined);
    const ownId = control && control.props.id;
    const controlId = ownId || htmlFor || (control ? baseId + '-control' : undefined);

    const extra = {};
    if (control) {
      if (!ownId) extra.id = controlId;
      const describedBy = [control.props['aria-describedby'], msgId].filter(Boolean).join(' ');
      if (describedBy) extra['aria-describedby'] = describedBy;
      if (error) extra['aria-invalid'] = true;
      if (required) extra['aria-required'] = true;
      if (combobox && label) extra['aria-labelledby'] = [control.props['aria-labelledby'], labelId].filter(Boolean).join(' ');
    }
    // один путь для любых детей — ключи не зависят от числа контролов (см. ⚠️ выше)
    const body = Children.map(children, (ch) => (control && isFieldControl(ch) ? cloneElement(ch, extra) : ch));
    // combobox: подпись без for — клик по ней только фокусирует триггер (APG), не открывает
    const focusCombobox = () => {
      if (typeof document === 'undefined' || !controlId) return;
      const el = document.getElementById(controlId);
      if (el && typeof el.focus === 'function') el.focus();
    };

    return (
      <div className={'field' + (error ? ' field--error' : '') + (className ? ' ' + className : '')}>
        {label && (
          <label className="field__label" id={labelId}
            htmlFor={combobox ? undefined : (control ? controlId : htmlFor)}
            onClick={combobox ? focusCombobox : undefined}>
            {label}{required && <span className="field__req" aria-hidden="true"> *</span>}
          </label>
        )}
        {body}
        {error
          ? <span className="field__msg field__msg--error" role="alert" id={msgId}><Icon name="alert-circle" size={13} />{error}</span>
          : hint && <span className="field__msg field__hint" id={msgId}>{hint}</span>}
      </div>
    );
  }

  /* ── TextInput ── */
  const TextInput = forwardRef(function TextInput({ invalid, className, ...rest }, ref) {
    return <input ref={ref} type="text"
      className={'input' + (invalid ? ' input--error' : '') + (className ? ' ' + className : '')}
      aria-invalid={invalid || undefined} {...rest} />;
  });

  /* ── PasswordInput ── */
  function PasswordInput({ invalid, className, ...rest }) {
    const [shown, setShown] = useState(false);
    return (
      <div className="input-affix">
        <input type={shown ? 'text' : 'password'}
          className={'input input--affix-r' + (invalid ? ' input--error' : '') + (className ? ' ' + className : '')}
          aria-invalid={invalid || undefined} {...rest} />
        <button type="button" className="input-affix__btn" onClick={() => setShown((s) => !s)}
          aria-label={shown ? 'Скрыть пароль' : 'Показать пароль'} tabIndex={-1}>
          <Icon name={shown ? 'eye-off' : 'eye'} size={16} />
        </button>
      </div>
    );
  }

  /* ── Textarea (авторост) ── */
  const Textarea = forwardRef(function Textarea({ minRows = 3, maxRows = 12, invalid, className, value, onChange, ...rest }, ref) {
    const innerRef = useRef(null);
    const el = ref || innerRef;
    const grow = () => {
      const node = el.current; if (!node) return;
      node.style.height = 'auto';
      const lh = parseFloat(getComputedStyle(node).lineHeight) || 20;
      const min = lh * minRows + 16;
      const max = lh * maxRows + 16;
      // пустое значение → минимум (не учитываем высоту обёрнутого placeholder — баг scrollHeight)
      const natural = node.value ? node.scrollHeight : min;
      node.style.height = Math.max(min, Math.min(natural, max)) + 'px';
      node.style.overflowY = natural > max ? 'auto' : 'hidden';
    };
    useEffect(grow, [value]);
    return (
      <textarea ref={el} rows={minRows} value={value}
        onChange={(e) => { onChange && onChange(e); grow(); }}
        className={'textarea' + (invalid ? ' input--error' : '') + (className ? ' ' + className : '')}
        aria-invalid={invalid || undefined} {...rest} />
    );
  });

  /* ── NumberInput: unit + stepper ──
     K-40: в RU разделитель дроби — запятая. Человек видит запятую, что бы ни набрал
     («12,5» или «12.5»), а в onChange уходит нормализованное значение с точкой — строка
     при наборе, число после blur. Хранимые значения не меняются. */
  function NumberInput({ value, onChange, unit, step = 1, min, max, invalid, disabled, plain, className, ...rest }) {
    // Пока поле в фокусе, показываем локальный текст набора: многие консьюмеры
    // хранят Number(v) и эхом рендерят его обратно — без этого запятая
    // исчезала при наборе («80,» → 80 → «80»), дробь было не ввести.
    // Контракт onChange не меняется: строка при вводе, clamped-число после blur.
    const [text, setText] = useState(toCommaDecimal(value));
    const focusRef = useRef(false);
    const inputRef = useRef(null);
    const caretRef = useRef(null);
    useEffect(() => {
      if (!focusRef.current) setText(toCommaDecimal(value));
    }, [value]);
    // «.» → «,» меняет значение поля, и браузер уводит каретку в конец. Длина та же —
    // возвращаем каретку туда, где человек печатал (правка в середине числа).
    useLayoutEffect(() => {
      const pos = caretRef.current;
      const el = inputRef.current;
      caretRef.current = null;
      if (pos == null || !el || typeof document === 'undefined' || document.activeElement !== el) return;
      try { el.setSelectionRange(pos, pos); } catch (e) { /* type без выделения */ }
    });
    const clamp = (v) => {
      if (v === '' || v == null) return v;
      let n = Number(v);
      if (isNaN(n)) return value;
      if (min != null) n = Math.max(min, n);
      if (max != null) n = Math.min(max, n);
      return n;
    };
    // Степперы +/- убраны во всех инпутах. Есть единица → число слева, единица справа;
    // нет единицы → число по центру.
    return (
      <div className={'num-input num-input--nostep' + (unit ? ' num-input--plain' : '') + (invalid ? ' num-input--error' : '') + (disabled ? ' is-disabled' : '') + (className ? ' ' + className : '')}>
        <input ref={inputRef} type="text" inputMode="decimal" className="num-input__field num" value={text}
          onFocus={() => { focusRef.current = true; }}
          onChange={(e) => {
            const raw = e.target.value;
            const t = normalizeDecimalText(raw);
            const shown = toCommaDecimal(t);
            if (shown.length === raw.length && e.target.selectionStart != null) caretRef.current = e.target.selectionStart;
            setText(shown);
            onChange && onChange(t);
          }}
          onBlur={(e) => {
            focusRef.current = false;
            const c = clamp(normalizeDecimalText(e.target.value));
            setText(toCommaDecimal(c));
            onChange && onChange(c);
          }}
          disabled={disabled} aria-invalid={invalid || undefined} {...rest} />
        {unit && <span className="num-input__unit num">{unit}</span>}
      </div>
    );
  }

  /* ── SearchInput: debounce + clear ──
     K-16: у поля есть имя. По умолчанию — из placeholder; ariaLabel сильнее; внутри Field
     (пришёл id) имя даёт видимая подпись, и aria-label её не перебивает. «Очистить» —
     обычная кнопка в порядке Tab; после очистки фокус возвращается в поле. */
  function SearchInput({ value, onChange, onDebouncedChange, delay = 300, placeholder = 'Поиск…', className, ariaLabel, ...rest }) {
    const [local, setLocal] = useState(value || '');
    const timer = useRef(null);
    const inputRef = useRef(null);
    const labelledElsewhere = rest.id != null || rest['aria-labelledby'] != null;
    const name = ariaLabel || rest['aria-label'] || (labelledElsewhere ? undefined : placeholder || 'Поиск');
    useEffect(() => { if (value != null) setLocal(value); }, [value]);
    const set = (v) => {
      setLocal(v);
      onChange && onChange(v);
      if (onDebouncedChange) {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => onDebouncedChange(v), delay);
      }
    };
    useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
    return (
      <div className={'search-input' + (className ? ' ' + className : '')}>
        <Icon name="search" size={16} className="search-input__icon" />
        <input ref={inputRef} type="search" className="input input--affix-l" value={local}
          onChange={(e) => set(e.target.value)} placeholder={placeholder} {...rest} aria-label={name} />
        {local && (
          <button type="button" className="search-input__clear" aria-label="Очистить поиск"
            onClick={() => {
              set('');
              if (onDebouncedChange) { if (timer.current) clearTimeout(timer.current); onDebouncedChange(''); }
              if (inputRef.current && inputRef.current.focus) inputRef.current.focus();
            }}>
            <Icon name="close" size={14} />
          </button>
        )}
      </div>
    );
  }

  /* ── NativeSelect ── */
  const NativeSelect = forwardRef(function NativeSelect({ options = [], invalid, className, children, ...rest }, ref) {
    return (
      <div className={'native-select' + (className ? ' ' + className : '')}>
        <select ref={ref} className={'select' + (invalid ? ' input--error' : '')} aria-invalid={invalid || undefined} {...rest}>
          {children || options.map((o) => {
            const opt = typeof o === 'string' ? { value: o, label: o } : o;
            return <option key={opt.value} value={opt.value}>{opt.label}</option>;
          })}
        </select>
        <Icon name="chevron-down" size={16} className="native-select__caret" />
      </div>
    );
  });

  /* ── Checkbox (нативный) ── */
  function Checkbox({ label, checked, onChange, disabled, indeterminate, className, ...rest }) {
    const ref = useRef(null);
    useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
    return (
      <label className={'checkbox' + (disabled ? ' is-disabled' : '') + (className ? ' ' + className : '')}>
        <input ref={ref} type="checkbox" className="checkbox__input" checked={checked}
          onChange={onChange} disabled={disabled} {...rest} />
        <span className="checkbox__box" aria-hidden="true"><Icon name="check" size={12} /></span>
        {label && <span className="checkbox__label">{label}</span>}
      </label>
    );
  }

  /* ── Segmented: pill-переключатель ──
     as="radio" (по умолчанию) — single-select форм-контрол: role=radiogroup/radio,
     aria-checked, навигация стрелками с roving tabindex (как у нативной radio-group).
     as="tab" — только для переключения панелей контента (есть tabpanel'ы), напр. DiffView.
     disabled — выбор виден, но не меняется (напр. замороженная аудитория тарифа с
     подписчиками): кнопки получают нативный disabled — ни клика, ни фокуса, ни стрелок.
     onb варианта — data-onb его кнопки: якорь подсказки тура онбординга (сегменты «Черновиков»). */
  function Segmented({ options = [], value, onChange, size = 'md', compact = false, className, ariaLabel, as = 'radio', disabled = false }) {
    const norm = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
    const isRadio = as !== 'tab';
    const move = (e, idx) => {
      if (disabled || !isRadio || !norm.length) return;
      let next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % norm.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + norm.length) % norm.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = norm.length - 1;
      if (next == null) return;
      e.preventDefault();
      onChange && onChange(norm[next].value);
      const root = e.currentTarget.parentNode;
      const btn = root && root.children[next];
      if (btn && btn.focus) btn.focus();
    };
    const activeIdx = norm.findIndex((o) => o.value === value);
    return (
      <div className={'segmented segmented--' + size + (compact ? ' segmented--compact' : '') + (disabled ? ' is-disabled' : '') + (className ? ' ' + className : '')}
        role={isRadio ? 'radiogroup' : 'tablist'} aria-label={ariaLabel} aria-disabled={disabled || undefined}>
        {norm.map((opt, idx) => {
          const active = opt.value === value;
          // roving tabindex для radio: фокусируем активный (или первый, если выбора нет)
          const tabIndex = isRadio ? ((active || (activeIdx < 0 && idx === 0)) ? 0 : -1) : undefined;
          // compact прячет подпись ТОЛЬКО у сегмента с иконкой (имя — в aria-label/title);
          // текстовый сегмент без подписи был бы пустой кнопкой без имени
          const iconOnly = compact && !!opt.icon && !!opt.label;
          return (
            <button type="button" key={opt.value}
              role={isRadio ? 'radio' : 'tab'}
              aria-checked={isRadio ? active : undefined}
              aria-selected={isRadio ? undefined : active}
              aria-label={iconOnly ? String(opt.label) : undefined}
              title={iconOnly ? String(opt.label) : undefined}
              tabIndex={tabIndex}
              disabled={disabled || undefined}
              data-onb={opt.onb || undefined}
              className={'segmented__item' + (iconOnly ? ' segmented__item--icon-only' : '') + (active ? ' is-active' : '')}
              onClick={() => !disabled && onChange && onChange(opt.value)}
              onKeyDown={(e) => move(e, idx)}>
              {opt.icon && <Icon name={opt.icon} size={16} />}
              {opt.label && <span className="segmented__label">{opt.label}</span>}
            </button>
          );
        })}
      </div>
    );
  }

  /* ── StrengthMeter: 4 сегмента ── */
  const STRENGTH = [
    { min: 0, label: 'Слабый', tone: 'bad', bars: 1 },
    { min: 2, label: 'Средний', tone: 'warn', bars: 2 },
    { min: 3, label: 'Хороший', tone: 'info', bars: 3 },
    { min: 4, label: 'Сильный', tone: 'ok', bars: 4 },
  ];
  function scorePassword(pw = '') {
    let s = 0;
    if (pw.length >= 8) s++;
    if (pw.length >= 12) s++;
    if (/[A-ZА-Я]/.test(pw) && /[a-zа-я]/.test(pw)) s++;
    if (/\d/.test(pw)) s++;
    if (/[^\w\s]/.test(pw)) s++;
    return Math.min(s, 5);
  }
  function StrengthMeter({ value = '', score }) {
    const sc = score != null ? score : scorePassword(value);
    if (!value) return <div className="strength" aria-hidden="true"><div className="strength__bars">{[0,1,2,3].map((i) => <span key={i} className="strength__bar" />)}</div></div>;
    const level = [...STRENGTH].reverse().find((l) => sc >= l.min) || STRENGTH[0];
    return (
      <div className={'strength strength--' + level.tone} role="status">
        <div className="strength__bars">
          {[0,1,2,3].map((i) => <span key={i} className={'strength__bar' + (i < level.bars ? ' is-on' : '')} />)}
        </div>
        <span className="strength__label">{level.label}</span>
      </div>
    );
  }

  /* ── Labeled: label + value read-only ── */
  function Labeled({ label, value, mono = false, className }) {
    return (
      <div className={'labeled' + (className ? ' ' + className : '')}>
        <span className="labeled__key t-caption">{label}</span>
        <span className={'labeled__val' + (mono ? ' num' : '')}>{value ?? '—'}</span>
      </div>
    );
  }

  // Метка «контрол поля»: Field пробрасывает сюда id/aria-* — каждый из них отдаёт их
  // своему нативному input/select/textarea (…rest). Новый контрол UI-kit — ставит метку.
  [TextInput, PasswordInput, Textarea, NumberInput, SearchInput, NativeSelect].forEach((C) => { C.fieldControl = true; });

export { Field };
export { TextInput };
export { PasswordInput };
export { Textarea };
export { NumberInput };
export { SearchInput };
export { NativeSelect };
export { Checkbox };
export { Segmented };
export { StrengthMeter };
export { Labeled };
export { scorePassword };
